import React, { useState } from 'react'

import FileCopy from '@mui/icons-material/FileCopy'
import {
  Button,
  CardContent,
  CardHeader,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Tooltip,
  Typography,
} from '@mui/material'
import ApiLimitsTable from 'features/customers/components/api/ApiLimitsTable'
import { DOMAIN_ALLOWLIST_DESCRIPTION, REVEAL_METERS } from 'features/customers/constants/apiMeters'
import { useCustomer } from 'features/customers/hooks/useCustomer'
import { useCustomerId } from 'features/customers/hooks/useCustomerId'
import { useCustomerRevealApiQuota } from 'features/customers/hooks/useCustomerRevealApiQuota'

import LoadingView from 'components/LoadingView'

import DeleteApiKeyModal from './DeleteApiKeyModal'
import GenerateApiKeyModal from './GenerateApiKeyModal'
import RevealApiLimitsModal from './RevealApiLimitsModal'

const MODALS = {
  manageLimits: 'manageLimits',
  generateNewApiKey: 'generateNewApiKey',
  deleteApiKey: 'deleteApiKey',
}

type ApiKeyInputProps = {
  label: string
  value: string
}

const ApiKeyInput = ({ label, value }: ApiKeyInputProps) => (
  <div style={{ marginBottom: 24 }}>
    <Typography style={{ marginBottom: 8 }} variant="subtitle1">
      {label}
    </Typography>
    <OutlinedInput
      disabled
      endAdornment={
        <InputAdornment position="start">
          <Tooltip arrow placement="top" title="Copy to clipboard">
            <IconButton aria-label="copy" onClick={() => navigator.clipboard.writeText(value)} size="large">
              <FileCopy />
            </IconButton>
          </Tooltip>
        </InputAdornment>
      }
      style={{ width: 455 }}
      value={value}
    />
  </div>
)

const RevealApi = () => {
  const [modal, setModal] = useState<any>(null)
  const customerId = useCustomerId()
  const { data: customer } = useCustomer(customerId)
  const {
    data: {
      requestsQuota = 0,
      identifiedQuota = 0,
      uniqueIdentifiedIpsQuota = 0,
      secretApiKey: rawSecretApiKey,
      publicApiKey,
      apiKey,
      publicApiKeyDomainAllowlist,
    } = {},
    isLoading: isQuotaLoading,
    isError,
  } = useCustomerRevealApiQuota()
  const handleModalClose = () => setModal(null)

  const secretApiKey = rawSecretApiKey ?? apiKey ?? null
  const hasApiKey = !!(publicApiKey || secretApiKey)

  const revealApiLimits = [
    {
      name: `${REVEAL_METERS.requests.label} limit`,
      description: REVEAL_METERS.requests.description,
      value: requestsQuota,
    },
    {
      name: `${REVEAL_METERS.identified.label} limit`,
      description: REVEAL_METERS.identified.description,
      value: identifiedQuota,
    },
    {
      name: `${REVEAL_METERS.uniqueIdentifiedIps.label} limit`,
      description: REVEAL_METERS.uniqueIdentifiedIps.description,
      value: uniqueIdentifiedIpsQuota,
    },
    {
      name: 'Domain Allowlist',
      description: DOMAIN_ALLOWLIST_DESCRIPTION,
      value: publicApiKeyDomainAllowlist?.join(', ') || 'Not set',
    },
  ]

  const limitsActions = (
    <Button
      onClick={() => {
        setModal(MODALS.manageLimits)
      }}>
      Manage
    </Button>
  )

  const apiKeyActions = !isQuotaLoading && (
    <>
      {hasApiKey && (
        <Button
          onClick={() => {
            setModal(MODALS.deleteApiKey)
          }}>
          Delete API key
        </Button>
      )}
      <Button
        onClick={() => {
          setModal(MODALS.generateNewApiKey)
        }}>
        Generate new key
      </Button>
    </>
  )

  const keysSection = () => {
    if (isError) return 'Error while loading Reveal API data'
    if (!hasApiKey) return 'No API key generated for this customer.'
    return (
      <>
        {publicApiKey && <ApiKeyInput label="Public Key" value={publicApiKey} />}
        {secretApiKey && <ApiKeyInput label="Private Key" value={secretApiKey} />}
      </>
    )
  }

  return (
    <div>
      <LoadingView loading={isQuotaLoading}>
        <CardHeader action={apiKeyActions} title="Reveal API" />
        <CardContent>{keysSection()}</CardContent>
      </LoadingView>
      {hasApiKey && (
        <>
          <CardHeader action={limitsActions} title="Limits" />
          <CardContent>
            {!isError ? <ApiLimitsTable rows={revealApiLimits} /> : 'Error while loading Reveal API limits data'}
          </CardContent>
        </>
      )}
      {modal === MODALS.deleteApiKey && <DeleteApiKeyModal onClose={handleModalClose} />}
      {modal === MODALS.manageLimits && (
        <RevealApiLimitsModal
          initialValues={{
            revealApiRequestsLimit: requestsQuota,
            revealApiIdentifiedLimit: identifiedQuota,
            revealApiUniqueIdentifiedIpsLimit: uniqueIdentifiedIpsQuota,
            publicApiKeyDomainAllowlist: publicApiKeyDomainAllowlist?.join(', ') || '',
          }}
          onClose={handleModalClose}
        />
      )}
      {modal === MODALS.generateNewApiKey && (
        <GenerateApiKeyModal
          initialValues={{
            revealApiRequestsLimit: requestsQuota || 1000000,
            revealApiIdentifiedLimit: identifiedQuota || 1000000,
            revealApiUniqueIdentifiedIpsLimit: uniqueIdentifiedIpsQuota || 1000000,
            apiKey: publicApiKey,
            publicApiKeyDomainAllowlist: publicApiKeyDomainAllowlist?.join(', ') || customer?.website || '',
          }}
          onClose={handleModalClose}
        />
      )}
    </div>
  )
}

export default RevealApi
